import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import type { SxProps, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import visuallyHidden from "@mui/utils/visuallyHidden";
import Cookies from "js-cookie";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
// biome-ignore lint/style/useImportType: False positive, because making React a type gives errors at all the MUI components
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { filter_cookies_set } from "../cookie-jar";
import TableBaseFilter, {
	type AdditionalFilterParams,
	type AdditionalFilterType,
	type OptionType,
	type TableBaseFilterRef,
} from "./table-base-filter";

/* -------------------------------------------------------------------------- */
/*                                    ENUMS                                   */
/* -------------------------------------------------------------------------- */
export enum TableIdEnum {
	AUDIT_LOG_TABLE = "AUDIT_LOG_TABLE",
	DEBTOR_TABLE_BACK_OFFICE_INDEX = "DEBTOR_TABLE_BACK_OFFICE_INDEX",
	DISCOUNT_OVERVIEW_BACK_OFFICE = "DISCOUNT_OVERVIEW_BACK_OFFICE",
	DOMAIN_OVERVIEW = "DOMAIN_OVERVIEW",
	DADUP_TABLE = "DADUP_TABLE",
	DADUP_BUCKET_TABLE = "DADUP_BUCKET_TABLE",
	DNS_TEMPLATE_TABLE = "DNS_TEMPLATE_TABLE",
	IP_BLACKLIST_BACK_OFFICE = "IP_BLACKLIST_BACK_OFFICE",
	IP_ADDRESS_TABLE = "IP_ADDRESS_TABLE",
	ORDER_TABLE = "ORDER_TABLE",
	PRODUCT_DOMAIN = "PRODUCT_DOMAIN",
	RELATION_OVERVIEW = "RELATION_OVERVIEW",
	PRODUCT_GROUP = "PRODUCT_GROUP",
	PRODUCT = "PRODUCT",
	PBS_TABLE = "PBS_TABLE",
	QEMU_OVERVIEW = "QEMU_OVERVIEW",
	SLA_TABLE = "SLA_TABLE",
	USER_TABLE_BACK_OFFICE = "USER_TABLE_BACK_OFFICE",
}

type TableSize = "small" | "medium";

type SortOptions = "asc" | "desc";

/* -------------------------------------------------------------------------- */
/*                                    TYPES                                   */
/* -------------------------------------------------------------------------- */
type ColumnType = {
	/**
	 * The key of the column, also used for `order_by` query param in the API calls
	 *
	 * It is important to make sure one element in the `row_list` has the same key name and is in the same order. Otherwise the **header** won't be properly matched to the correct **cell**
	 *
	 * Note: If you need to overwrite the key used for sorting using the `order_by` query param, use the `sort_key` property
	 */
	key: string;

	/** The label of the column, shows within the table header */
	label: string;

	/** Optional boolean to hide the column and it's values. */
	hidden?: boolean;

	/** Optional flag indicator to make a column sortable or not (Default: `true`) */
	sortable?: boolean;

	/** Optional flag to overwrite the key that is used in the `order_by` query param in the API calls. */
	sort_key?: string;

	/** Optional flag to overwrite the auto width of a column.  */
	width?: string;
};

type CellType = {
	/**
	 * The key of the cell.
	 *
	 * It is important to make sure one element in the `column_list` has the same key name and is in the same order. Otherwise the **cell** won't be properly matched to the correct **header**
	 */
	key: string;
	value: string | JSX.Element;
};

type RowType = {
	sx?: SxProps<Theme>;
	cell_list: CellType[];
	nested_component?: JSX.Element;
};

/* -------------------------------------------------------------------------- */
/*                                 TABLE BASE                                 */

type OnChangeParams = {
	limit?: number;
	offset?: number;
	query?: string;
	order_by?: string;
	sort?: SortOptions;
	[key: string]: string | number | boolean | undefined;
};

type TableBaseProps = {
	/** Optional additional filters for the table. Uses the key as query param */
	additional_filters: AdditionalFilterType[];

	/** Optional flag to determ the position of the additional filters */
	additional_filters_own_line?: boolean;

	/** List of columns to display in the table. Each column can optionally be sortable (Default: `true`). */
	column_list: ColumnType[];

	/** Enabled reset button when enabled */
	enable_reset_button?: boolean;

	/** Enables search field when enabled */
	enable_search?: boolean;

	/** Function triggered on table change. */
	on_change?: (params: Required<OnChangeParams>, setLoading: (value: boolean) => void) => void;

	/** Indicates the order_by default (Default: The `key` from the 1st item in the `column_list`) */
	order_by_default?: string;

	/** Containing the rows to show */
	row_list: RowType[];

	/** Indicates the sort default. (Default: `'asc'`) */
	sort_default?: SortOptions;

	/** Optional table ID used to store data about the table such as default limit */
	table_id?: TableIdEnum;

	/** Indicates the size of the table (Default: `'small'`) */
	table_size?: TableSize;

	/** Indicates the total rows that are available*/
	total_count: number;

	/** When enabled, filter values are applied to the URL as query parameters */
	url_params_enabled?: boolean;
};

export type TableBaseRef = {
	triggerUpdate: (params?: Partial<OnChangeParams>) => void;
};

const TableBase = forwardRef<TableBaseRef, TableBaseProps>(
	(
		{
			additional_filters = [],
			additional_filters_own_line,
			column_list = [],
			enable_reset_button = false,
			enable_search = false,
			on_change = () => {},
			order_by_default = column_list[0]?.sort_key ?? column_list[0]?.key,
			row_list = [],
			sort_default = "asc",
			table_id = null,
			table_size = "small",
			total_count = 0,
			url_params_enabled = true,
		},
		ref,
	) => {
		// Helper functions
		const attemptCookieFetchHandler = (key: string, defaultValue: number): number => {
			if (!table_id) {
				return defaultValue;
			}

			const cookie = Cookies.get(key);

			if (!cookie) {
				return defaultValue;
			}

			const parsed = Number.parseInt(cookie, 10);
			return Number.isNaN(parsed) ? defaultValue : parsed;
		};

		// Constants
		const CookieLimit = `${table_id}_LIMIT`;
		const DebounceDelay = 250;

		// Hooks
		const pathname = usePathname();
		const searchParams = useSearchParams();
		const router = useRouter();

		const [loading, setLoading] = useState(true);
		const [offset, setOffset] = useState(0);
		const [limit, setLimit] = useState(attemptCookieFetchHandler(CookieLimit, 50));
		const [orderBy, setOrderBy] = useState(order_by_default);
		const [query, setQuery] = useState("");
		const [sort, setSort] = useState<SortOptions>(sort_default);
		const [paramsAdditional, setParamsAdditional] = useState({});
		const [previousParams, setPreviousParams] = useState(null);
		const [nestedTableActiveKey, setNestedTableActiveKey] = useState(null);

		const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
		const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

		const additionalFilterRef = useRef<TableBaseFilterRef>(null);

		/**
		 * Helper function to set the loading state after a predefined delay (500ms).
		 *
		 * When an API call is made, the loading state will only be shown after the predefined delay.
		 * This prevent the table components from flashing quickly from loading state to rendered state when an API call is fast.
		 *
		 * @param should_load  Flag to set the loading state to either `true` or `false`. The delay is only applied when the value is `true`. When the value is `false`, the loading state is disabled immediately with the timeout reference being cleared.
		 */
		const setLoadingWithDelay = (shouldLoad: boolean) => {
			if (shouldLoad === true) {
				// Clear any existing loading timeout
				if (loadingTimeoutRef.current) {
					clearTimeout(loadingTimeoutRef.current);
				}

				// Set loading to true after 500ms delay
				loadingTimeoutRef.current = setTimeout(() => {
					setLoading(true);
				}, 500);
			} else {
				// Clear loading timeout if setting to false
				if (loadingTimeoutRef.current) {
					clearTimeout(loadingTimeoutRef.current);
					loadingTimeoutRef.current = null;
				}
				// Set loading to false immediately
				setLoading(false);
			}
		};

		// Handlers
		const onUpdateHandler = (
			paramsRaw: OnChangeParams,
			paramsAdditionalNew?: AdditionalFilterParams,
		) => {
			setLoadingWithDelay(true);
			setNestedTableActiveKey(null);

			// Determ additional params
			const params = prepareParams(paramsRaw, paramsAdditionalNew);

			// Trigger update
			if (params !== previousParams) {
				setPreviousParams(params);
				on_change(params, setLoadingWithDelay);
			}

			if (url_params_enabled) {
				onUpdateSearchParams(params);
			}
		};
		const prepareParams = (
			paramsRaw: OnChangeParams,
			paramsAdditionalNew?: AdditionalFilterParams,
		) => {
			// Determ additional params
			const additionalParams = paramsAdditionalNew ?? paramsAdditional;

			return {
				limit: paramsRaw.limit ?? limit,
				offset: paramsRaw.offset ?? offset,
				order_by: paramsRaw.order_by ?? orderBy,
				query: paramsRaw.query ?? query,
				sort: paramsRaw.sort ?? sort,
				...additionalParams,
			};
		};

		const onUpdateSearchParams = (params: OnChangeParams) => {
			// Prevent URL updates if router is already navigating
			if (router.isReady === false) {
				return;
			}

			const queryParams = new URLSearchParams();

			// Always set the table_id first if it exists
			if (table_id) {
				queryParams.set("table_id", table_id);
			}

			// Add all other parameters without namespacing
			for (const [key, value] of Object.entries(params)) {
				const searchParam = encodeURIComponent(value);

				if (!searchParam) {
					continue; // Skip empty params
				}

				queryParams.set(key, searchParam);
			}

			const newUrl = `${pathname}?${queryParams.toString()}`;

			// Only update if the URL is actually different
			if (window.location.pathname + window.location.search !== newUrl) {
				router.replace(newUrl, undefined, { shallow: true });
			}
		};

		const onUpdateHandlerWithSearchParams = () => {
			// Check if the URL parameters belong to this table
			const urlTableId = searchParams.get("table_id");

			// If there's a table_id in the URL and it doesn't match this table, don't load from URL
			if (urlTableId && urlTableId !== table_id) {
				// Use default values and trigger update
				onUpdateHandler({});
				return;
			}

			// If no table_id in URL but this table has an ID, don't load from URL
			if (!urlTableId && table_id) {
				onUpdateHandler({});
				return;
			}

			// Load parameters from URL
			const params = {};
			const params_additional = {};

			for (const key of searchParams.keys()) {
				// Skip the table_id parameter itself
				if (key === "table_id") {
					continue;
				}

				const searchParam = decodeURIComponent(searchParams.get(key));
				const additionalFilter = isAdditionalFilter(key);

				if (additionalFilter) {
					params_additional[key] = searchParam;
					updateAdditionalFilterValue(additionalFilter, searchParam);
					continue;
				}

				updateParamFilterValue(key, searchParam);
				params[key] = searchParam;
			}

			onUpdateHandler(params, params_additional);
		};

		const isAdditionalFilter = (key: string) => {
			return additional_filters.find((filter) => filter.key === key);
		};

		const updateParamFilterValue = (key: string, searchParam: string) => {
			switch (key) {
				case "query":
					setQuery(searchParam);
					break;
				case "order_by":
					setOrderBy(searchParam);
					break;
				case "sort":
					setSort(searchParam as SortOptions);
					break;
				case "offset":
					setOffset(Number.parseInt(searchParam, 10) || 0);
					break;
				case "limit":
					setLimit(Number.parseInt(searchParam, 10) || 50);
					break;
			}
		};

		const updateAdditionalFilterValue = (
			additionalFilter: AdditionalFilterType,
			searchParam: string,
		) => {
			let defaultValue: string | number | boolean | OptionType[];

			switch (additionalFilter.type) {
				case "multi-select":
					defaultValue = searchParam.split(",").map((v) => ({ label: v, value: v }));
					break;

				default:
					defaultValue = searchParam;
			}
			if (additionalFilterRef.current) {
				return additionalFilterRef.current.setFieldValue(additionalFilter.key, defaultValue);
			}
		};

		const onOrderByHandler = (columnKey: string) => {
			const isSameColumn = orderBy === columnKey;
			const newSort: SortOptions = isSameColumn && sort === "asc" ? "desc" : "asc";

			setSort(newSort);
			setOrderBy(columnKey);

			onUpdateHandler({ order_by: columnKey, sort: newSort });
		};

		const onOffsetChangeHandler = (page: number) => {
			const offsetNew = page * limit;

			setOffset(offsetNew);
			onUpdateHandler({ offset: offsetNew });
		};

		const onLimitChangeHandler = (limit: number) => {
			// Update cookie when table id provided
			if (table_id) {
				filter_cookies_set(CookieLimit, limit);
			}

			setOffset(0);
			setLimit(limit);
			onUpdateHandler({ limit: limit });
		};

		const onSearchHandler = (query: string) => {
			setOffset(0);
			setQuery(query);

			// Clear existing timeout
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}

			// Set new timeout with debounce
			debounceTimeoutRef.current = setTimeout(() => {
				onUpdateHandler({ offset: 0, query: query });
			}, DebounceDelay);
		};

		const onResetButtonHandler = () => {
			const resetParams = onResetParams();
			onResetParamsAdditional();

			onUpdateHandler(resetParams, {});
		};

		const onResetParams = () => {
			const resetValues = {
				offset: 0,
				order_by: order_by_default,
				query: "",
				sort: sort_default,
			};

			setOffset(resetValues.offset);
			setOrderBy(resetValues.order_by);
			setSort(resetValues.sort);
			setQuery(resetValues.query);

			return resetValues;
		};

		const onResetParamsAdditional = () => {
			if (additionalFilterRef.current) {
				additionalFilterRef.current.resetForm();
				setParamsAdditional({});
			}
		};

		const onAdditionalFilterHandler = (params_additional: AdditionalFilterParams) => {
			setParamsAdditional(params_additional);
			onUpdateHandler({}, params_additional);
		};

		/* eslint-disable */
		// biome-ignore lint/correctness/useExhaustiveDependencies: It's required to trigger the update handler once, so we can update the content from all the tables from one section instead of managing it from the parent components.
		useEffect(() => {
			setTimeout(() => {
				url_params_enabled ? onUpdateHandlerWithSearchParams() : onUpdateHandler({});
			}, 100);
		}, []);
		/* eslint-enable */

		useImperativeHandle(ref, () => ({
			triggerUpdate: (params = {}) => {
				setQuery(params?.query || query);
				setOffset(0);
				onUpdateHandler({ ...params, offset: 0 });
			},
		}));

		return (
			<Box>
				<TableBaseFilter
					ref={additionalFilterRef}
					additional_filters_own_line={additional_filters_own_line}
					on_additional_filter_handler={onAdditionalFilterHandler}
					additional_filters={additional_filters}
					enable_search={enable_search}
					enable_reset_button={enable_reset_button}
					loading={loading}
					on_search_handler={onSearchHandler}
					on_reset_button_handler={onResetButtonHandler}
					query={query}
					table_id={table_id}
				/>

				<Paper variant="outlined">
					<TableContainer>
						<Table size={table_size} sx={{ tableLayout: { xl: "fixed" } }}>
							<TableBaseHeader
								on_order_by_handler={onOrderByHandler}
								column_list={column_list}
								row_list={row_list}
								loading={loading}
								order_by={orderBy}
								sort={sort}
							/>

							{loading ? (
								Array.from({ length: limit }).map((_, i) => (
									<TableBaseRowLoading
										row_key={`${i}`}
										row={{ cell_list: [] }}
										column_list={column_list}
										// biome-ignore lint/suspicious/noArrayIndexKey: We have no key
										key={i}
										nested_table_active_key={nestedTableActiveKey}
										set_nested_table_active_key={setNestedTableActiveKey}
									/>
								))
							) : (
								<TableBody>
									{row_list.length <= 0 ? (
										<TableBaseRowEmpty
											row_key="empty"
											row={{ cell_list: [] }}
											column_list={column_list}
											nested_table_active_key={nestedTableActiveKey}
											set_nested_table_active_key={setNestedTableActiveKey}
										/>
									) : (
										row_list.map((row, i) => (
											<TableBaseRow
												// biome-ignore lint/suspicious/noArrayIndexKey: No self created key can be provided
												key={`${i}`}
												row={row}
												column_list={column_list}
												row_key={`${i}`}
												nested_table_active_key={nestedTableActiveKey}
												set_nested_table_active_key={setNestedTableActiveKey}
											/>
										))
									)}
								</TableBody>
							)}
						</Table>

						<TableBasePagination
							loading={loading}
							offset={offset}
							on_offset_change_handler={onOffsetChangeHandler}
							on_limit_change_handler={onLimitChangeHandler}
							total_count={total_count}
							limit={limit}
						/>
					</TableContainer>
				</Paper>
			</Box>
		);
	},
);
TableBase.displayName = "TableBase";
export default TableBase;

/* -------------------------------------------------------------------------- */
/*                                TABLE HEADER                                */
/* -------------------------------------------------------------------------- */
type TableHeaderProps = {
	column_list: ColumnType[];
	row_list: RowType[];
	loading: boolean;
	on_order_by_handler: (columnKey: string) => void;
	order_by: string;
	sort: SortOptions;
};

function TableBaseHeader({
	column_list = [],
	row_list = [],
	loading,
	on_order_by_handler,
	order_by,
	sort,
}: TableHeaderProps): JSX.Element {
	return (
		<TableHead>
			<TableRow>
				{/* Add extra column when nested table used  */}
				{row_list.some((row) => row.nested_component) && <TableCell sx={{ width: "50px" }} />}
				{column_list.map((column) => {
					if (isHidden(column)) {
						return null;
					}

					return (
						<TableBaseHeaderCell
							loading={loading}
							on_order_by_handler={on_order_by_handler}
							key={column.key}
							column={column}
							order_by={order_by}
							sort={sort}
						/>
					);
				})}
			</TableRow>
		</TableHead>
	);
}

type TableHeadCellProps = {
	column: ColumnType;
	loading: boolean;
	on_order_by_handler: (columnKey: string) => void;
	order_by: string;
	sort: SortOptions;
};

function TableBaseHeaderCell({
	column,
	loading,
	on_order_by_handler,
	order_by,
	sort,
}: TableHeadCellProps): JSX.Element {
	const isSortable = column.sortable ?? true;

	if (!isSortable) {
		return (
			<TableCell sx={column.width ? { width: column.width } : undefined}>{column.label}</TableCell>
		);
	}

	const columnKey = column?.sort_key ?? column.key;
	return (
		<TableCell sx={column.width ? { width: column.width } : undefined}>
			<TableSortLabel
				active={order_by === columnKey}
				direction={order_by === columnKey ? sort : "asc"}
				disabled={loading}
				onClick={() => on_order_by_handler(columnKey)}
			>
				{column.label}
				{order_by === columnKey ? (
					<Box component="span" sx={visuallyHidden}>
						{sort === "desc" ? "sorted descending" : "sorted ascending"}
					</Box>
				) : null}
			</TableSortLabel>
		</TableCell>
	);
}

/* -------------------------------------------------------------------------- */
/*                               TRUNCATED CELL                               */
/* -------------------------------------------------------------------------- */
/**
 * Cell with logic to truncate text when the text exceeds the cell width.
 *
 * When the text doesn't exceed the cell width, it will be displayed like normal.
 *
 * When the text exceeds the cell width, it will be truncated and a tooltip with the full text will be displayed on hover. When the window is resized or the cell value is updated (e.g. search query performed), the table cells will be rechecked if a tooltip is needed.
 *
 * @param cell_value - The string value of the cell
 * @returns - JSX Element with the content of the cell
 */
function TruncatedCell({ cell_value }: { cell_value: string }): JSX.Element {
	const [isTruncated, setIsTruncated] = useState(false);
	const textRef = useRef<HTMLDivElement>(null);

	const getPreciseTextWidth = useCallback((text: string, element: HTMLElement): number => {
		const canvas = document.createElement("canvas");
		const context = canvas.getContext("2d");

		if (!context) {
			return 0;
		}

		const computedStyles = window.getComputedStyle(element);
		context.font = `${computedStyles.fontWeight} ${computedStyles.fontSize} ${computedStyles.fontFamily}`;

		return context.measureText(text).width;
	}, []);

	useEffect(() => {
		const checkTruncation = () => {
			if (textRef.current) {
				// Get the width of the text and the container as floats
				// This level of accuracy is necessary to prevent a situation where the text is truncated but the tooltip is not shown (happens when using `text_ref.current.scrollWidth` and `text_ref.current.clientWidth`, because those values are integers)
				const containerWidth = textRef.current.getBoundingClientRect().width;
				const preciseTextWidth = getPreciseTextWidth(cell_value, textRef.current);

				const isContentTruncated = preciseTextWidth > containerWidth;
				setIsTruncated(isContentTruncated);
			}
		};

		checkTruncation();
		window.addEventListener("resize", checkTruncation);

		return () => {
			window.removeEventListener("resize", checkTruncation);
		};
	}, [cell_value, getPreciseTextWidth]);

	const truncatedContent = (
		<Box
			ref={textRef}
			sx={{
				maxWidth: "100%",
				// Truncate the text when it exceeds the cell width
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
			}}
		>
			{cell_value}
		</Box>
	);
	// Only when the text is truncated, show a tooltip with the full text
	if (isTruncated) {
		return (
			<Tooltip title={cell_value} arrow placement="top">
				{truncatedContent}
			</Tooltip>
		);
	}

	return truncatedContent;
}

/* -------------------------------------------------------------------------- */
/*                               TABLE BASE ROW                               */
/* -------------------------------------------------------------------------- */
type TableBaseRowProps = {
	row_key: string;
	row: RowType;
	column_list: ColumnType[];
	nested_table_active_key: string;
	set_nested_table_active_key: (key?: string) => void;
};

function TableBaseRow({
	row_key,
	row,
	column_list,
	nested_table_active_key,
	set_nested_table_active_key,
}: TableBaseRowProps): JSX.Element {
	const renderCellContent = (cellValue: string | JSX.Element) => {
		// If the cell value is a JSX element, return it as-is
		if (typeof cellValue !== "string") {
			return cellValue;
		}
		// Render the cell with logic to truncate the text when it exceeds the cell width
		return <TruncatedCell cell_value={cellValue} />;
	};

	return (
		<>
			<TableRow sx={row?.sx || {}}>
				{row?.nested_component && (
					<TableCell>
						<IconButton
							aria-label="expand row"
							size="small"
							onClick={() => {
								set_nested_table_active_key(row_key === nested_table_active_key ? null : row_key);
							}}
						>
							{row_key === nested_table_active_key ? (
								<KeyboardArrowUpIcon />
							) : (
								<KeyboardArrowDownIcon />
							)}
						</IconButton>
					</TableCell>
				)}

				{column_list.map((column) => {
					const cell = row.cell_list.find((c) => c.key === column.key);

					if (isHidden(column)) {
						return null;
					}

					return (
						<TableCell key={column.key}>
							{cell?.value ? renderCellContent(cell.value) : "-"}
						</TableCell>
					);
				})}
			</TableRow>

			<TableRow>
				{row?.nested_component && (
					<TableCell
						sx={{ paddingBottom: 0, paddingTop: 0 }}
						colSpan={column_list.filter((column) => !isHidden(column)).length + 1}
					>
						<Collapse in={row_key === nested_table_active_key}>
							{/* Nested table  */}
							{row.nested_component}
						</Collapse>
					</TableCell>
				)}
			</TableRow>
		</>
	);
}

function TableBaseRowLoading({ column_list }: TableBaseRowProps): JSX.Element {
	return (
		<TableBody>
			<TableRow>
				{column_list.map((column) => {
					if (isHidden(column)) {
						return null;
					}

					return (
						<TableCell key={column.key}>
							<Skeleton />
						</TableCell>
					);
				})}
			</TableRow>
		</TableBody>
	);
}

function TableBaseRowEmpty({ column_list }: TableBaseRowProps): JSX.Element {
	const { t } = useTranslation();

	return (
		<TableRow>
			<TableCell colSpan={column_list.filter((column) => !isHidden(column)).length}>
				<Box textAlign="center" display="flex" justifyContent="center">
					<SearchOffIcon />
					<Typography>{t("table_filter.table_row_empty")}</Typography>
				</Box>
			</TableCell>
		</TableRow>
	);
}

/* -------------------------------------------------------------------------- */
/*                              TABLE PAGINATION                              */
/* -------------------------------------------------------------------------- */
type TableBasePaginationProps = {
	offset: number;
	loading: boolean;
	limit: number;
	on_limit_change_handler: (page: number) => void;
	on_offset_change_handler: (page: number) => void;
	total_count: number;
};

function TableBasePagination({
	loading,
	offset,
	on_limit_change_handler,
	on_offset_change_handler,
	limit,
	total_count,
}: TableBasePaginationProps): JSX.Element {
	const { t } = useTranslation();

	const RowsPerPageOptions = [5, 10, 25, 50];

	return (
		<TablePagination
			disabled={loading}
			rowsPerPageOptions={RowsPerPageOptions}
			component="div"
			count={total_count}
			rowsPerPage={limit}
			page={Math.floor(offset / limit)}
			onPageChange={(_, page) => on_offset_change_handler(page)}
			onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
				on_limit_change_handler(Number.parseInt(event.target.value, 10))
			}
			labelRowsPerPage={t("table_filter.pagination.rows_per_page")}
			labelDisplayedRows={({ from, to, count }) =>
				`${from}-${to} ${t("table_filter.pagination.from")} ${count}`
			}
		/>
	);
}
/**
 * Checks if a column should be hidden
 *
 * @remarks
 * This function was primarily made to fix confusion where situations with `!== true` would be written as `=== false` and create a bug where the columns wouldn't be shown.
 */
function isHidden(column: ColumnType): boolean {
	return column?.hidden === true;
}
